import { Attribute, Relationship, Resource } from 'json-api';
import { AtomResource } from '../classes/atom-resource';
import { Enum } from './enum.resource';
import { Platform } from './platform.resource';
import { Product } from './product.resource';
import { Firm } from './firm.resource';
import { Country } from './country.resource';
import { BillingChannel } from './billing-channel.resource';
import { OrderChannel } from './order-channel.resource';
import { Offer } from './offer.resource';
import { Service } from './service.resource';
import { NumberFilter, StringFilter } from 'src/app/filter/filter';
import {
  BillingChannelConfig,
  FirmConfig,
  OfferConfig,
  OrderChannelConfig,
  ProductConfig,
  ServiceConfig,
  CountryConfig,
} from 'src/app/shared/tools/search/search-config';
import { AtomService } from '../atom.service';
import {
  ResourceConfig,
  ResourceConfigTab,
} from 'src/app/resources/resources-interfaces/resource-config';

@Resource({
  type: 'notificationmanagementsdvp4m',
})
export class Notificationmanagementsdvp4m extends AtomResource {
  @Attribute({ key: 'subscription_arn' }) subscriptionArn?: string;
  @Attribute({ key: 'notification_url' }) notificationUrl?: string;
  @Attribute({ key: 'secret_key' }) secretKey?: string;
  @Attribute() mode?: Enum;
  @Attribute({ key: 'event_types' }) eventTypes?: Enum[];
  @Attribute({ key: 'event_statuses' }) eventStatuses?: Enum[];
  @Attribute() data?: Record<string, unknown>;

  @Relationship() firm?: Firm;
  @Relationship() countries?: Country[];
  @Relationship() billingchannels?: BillingChannel[];
  @Relationship() orderchannels?: OrderChannel[];
  @Relationship() eventplatforms?: Platform[];
  @Relationship() offers?: Offer[];
  @Relationship() products?: Product[];
  @Relationship() services?: Service[];
  @Relationship() handlerfirms?: Firm[];
  @Relationship() ownerfirm?: Firm;
}
export class NotificationConfig implements ResourceConfig {
  tables = {
    includes: [
      'firm.headquarterscountry',
      'countries',
      'ownerfirm.headquarterscountry',
    ],
    columns: [
      {
        columnDef: 'id',
        header: 'ID',
        displayedDatas: [{ path: ['id'], class: 'main', translatable: false }],
        displayed: true,
        link: {
          detail: true,
        },
      },
      {
        columnDef: 'firm.name',
        displayedDatas: [
          {
            path: ['firm.name'],
            class: 'mat-body-2',
            translatable: true,
          },
          {
            path: ['firm.headquarterscountry.name'],
            class: 'mat-body-1',
            translatable: true,
          },
        ],
        header: 'Firm',
        displayed: true,
        link: {
          resource: 'firms',
          filter: 'firm.id',
          icon: 'business',
        },
      },
      {
        columnDef: 'notificationUrl',
        header: 'URL',
        displayedDatas: [
          {
            path: ['notificationUrl'],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        displayed: true,
        link: {
          external: true,
          href: 'notificationUrl',
        },
      },
      {
        columnDef: 'secretKey',
        header: 'Secret Key',
        displayedDatas: [
          { path: ['secretKey'], class: 'mat-body-2', translatable: false },
        ],
        displayed: true,
        options: {
          copy: true,
        },
      },
      {
        columnDef: 'subscriptionArn',
        header: 'Subsription ARN',
        displayedDatas: [
          {
            path: ['subscriptionArn'],
            class: 'mat-body-2',
            translatable: false,
          },
        ],
        displayed: false,
        link: {
          update: 'notificationmanagementsdvp4m/subscription/update/',
          create: 'notificationmanagementsdvp4m/subscription/create/',
        },
        options: {
          copy: true,
        },
      },
      {
        columnDef: 'mode',
        header: 'Mode',
        displayedDatas: [
          { path: ['mode'], class: 'mat-body-1', translatable: false },
        ],
        displayed: true,
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'notifications',
      title: [
        {
          path: ['firm.name'],
          translatable: true,
        },
      ],
      id: true,
    },
    elements: [
      {
        title: 'End Points',
        showEmpty: true,
        list: [
          {
            icon: 'link',
            title: 'Notification Url',
            APIKey: 'notificationUrl',
            value: {
              path: ['notificationUrl'],
              translatable: false,
            },
          },
          {
            icon: 'business',
            title: 'Firm',
            APIKey: 'firm',
            value: {
              path: ['firm.name'],
              translatable: true,
            },
            link: {
              resource: 'firms',
              filter: 'firm.id',
            },
          },
          {
            icon: 'key',
            title: 'Secret Key',
            APIKey: 'secretKey',
            value: {
              path: ['secretKey'],
              translatable: false,
            },
            options: {
              copy: true,
            },
          },
          {
            icon: 'priority_high',
            title: 'Mode',
            APIKey: 'mode',
            value: {
              path: ['mode'],
              translatable: false,
            },
          },
        ],
      },
      {
        title: 'Events',
        list: [
          {
            title: 'Type',
            type: 'chips',
            APIKey: 'eventTypes',
            value: {
              path: ['eventTypes'],
              translatable: false,
            },
          },
          {
            title: 'Status',
            type: 'chips',
            APIKey: 'eventStatuses',
            value: {
              path: ['eventStatuses'],
              translatable: false,
            },
          },
        ],
      },
      {
        title: 'Triggers',
        list: [
          {
            title: 'Owner Firm',
            type: 'chips',
            APIKey: 'ownerfirm',
            value: {
              path: ['name'],
              translatable: true,
            },
            link: {
              resource: 'firms',
              filter: 'firm.id',
            },
          },
          {
            title: 'Countries',
            type: 'chips',
            APIKey: 'countries',
            value: {
              path: ['name'],
              translatable: true,
            },
            link: {
              resource: 'countries',
              filter: 'country.id',
            },
          },
          {
            title: 'Services',
            type: 'chips',
            APIKey: 'services',
            value: {
              path: ['name'],
              translatable: true,
            },
            link: {
              resource: 'services',
              filter: 'service.id',
            },
          },
          {
            title: 'Products',
            type: 'chips',
            APIKey: 'products',
            value: {
              path: ['name'],
              translatable: true,
            },
            link: {
              resource: 'products',
              filter: 'product.id',
            },
          },
          {
            title: 'Offers',
            type: 'chips',
            APIKey: 'offers',
            value: {
              path: ['name'],
              translatable: true,
            },
            link: {
              resource: 'offers',
              filter: 'offer.id',
            },
          },
          {
            title: 'Order Channels',
            type: 'chips',
            APIKey: 'orderchannels',
            value: {
              path: ['name'],
              translatable: true,
            },
          },
          {
            title: 'Billing Channels',
            type: 'chips',
            APIKey: 'billingchannels',
            value: {
              path: ['name'],
              translatable: true,
            },
          },
        ],
      },
    ],
  };
  wizard: {
    tabs?: ResourceConfigTab[];
    linear?: boolean;
  } = {
    linear: true,
    tabs: [
      {
        title: 'End Points',
        formGroup: 'endpoints',
        list: [
          {
            type: 'text',
            title: 'Notification URL',
            formControl: 'notificationUrl',
            APIKey: 'notification_url',
            translatable: false,
            required: true,
            check: 'equal',
          },
          {
            type: 'resource',
            title: 'Firm',
            formControl: 'firm',
            search: new FirmConfig(this.atom),
            required: true,
            check: 'equal',
          },
          {
            type: 'text',
            title: 'Secret Key',
            formControl: 'secretKey',
            APIKey: 'secret_key',
            translatable: false,
            required: true,
            options: {
              copy: true,
              api: 'generate',
              path: 'secretkey',
              key: 'secret_key',
            },
            check: 'equal',
          },
          {
            type: 'enum',
            title: 'Mode',
            formControl: 'mode',
            APIKey: 'mode',
            fieldKey: 'mode',
            multiple: false,
            required: false,
          },
        ],
      },
      {
        title: 'Events',
        formGroup: 'events',
        list: [
          {
            type: 'enum',
            title: 'Event Type',
            formControl: 'eventTypes',
            APIKey: 'event_types',
            fieldKey: 'event_types',
            multiple: true,
            required: true,
            check: 'like',
          },
          {
            type: 'enum',
            title: 'Event Statuses',
            formControl: 'eventStatuses',
            APIKey: 'event_statuses',
            fieldKey: 'event_statuses',
            multiple: true,
            required: true,
            check: 'like',
          },
        ],
      },
      {
        title: 'Triggers',
        formGroup: 'triggers',
        options: {
          create: true,
        },
        list: [
          {
            type: 'resource',
            title: 'Owner Firm',
            formControl: 'ownerfirm',
            multiple: false,
            required: false,
            search: new FirmConfig(this.atom, false),
            check: 'equal',
            noCols: true,
            linkedValue: {
              group: 'endpoints',
              control: 'firm',
            },
            activatingSequence: [
              {
                group: 'triggers',
                control: 'services',
                noCheckResult: true,
                noNullIfInactivated: true,
                filters: {
                  filter: 'equal.ownerfirm.id',
                },
              },
              {
                group: 'triggers',
                control: 'products',
                noCheckResult: true,
                noNullIfInactivated: true,
                filters: {
                  filter: 'equal.service.ownerfirm.id',
                },
              },
              {
                group: 'triggers',
                control: 'offers',
                noCheckResult: true,
                noNullIfInactivated: true,
                filters: {
                  filter: 'equal.product.service.ownerfirm.id',
                },
              },
            ],
          },
        ],
        radio: [
          {
            type: 'resource',
            title: 'Country',
            formControl: 'countries',
            multiple: true,
            required: false,
            search: new CountryConfig(this.atom, true),
            check: 'equal',
            noCols: true,
          },
          {
            type: 'resource',
            title: 'Services',
            formControl: 'services',
            multiple: true,
            required: false,
            search: new ServiceConfig(this.atom, true),
            check: 'equal',
            noCols: true,
            // filter: [
            //   {
            //     key: 'equal.ownerfirm.id',
            //     value: 'ownerfirm.id'
            //   }
            // ]
          },
          {
            type: 'resource',
            title: 'Products',
            formControl: 'products',
            multiple: true,
            required: false,
            search: new ProductConfig(this.atom, true),
            check: 'equal',
            noCols: true,
          },
          {
            type: 'resource',
            title: 'Offers',
            formControl: 'offers',
            multiple: true,
            required: false,
            search: new OfferConfig(this.atom, true),
            check: 'equal',
            noCols: true,
          },
        ],
      },
      {
        title: 'Advance Triggers',
        formGroup: 'advancetriggers',
        options: {
          restriction: {
            role: 'admin',
          },
        },
        list: [
          {
            type: 'resource',
            title: 'Order Channel',
            formControl: 'orderchannels',
            multiple: true,
            required: false,
            search: new OrderChannelConfig(true),
            check: 'equal',
            noCols: true,
          },
          {
            type: 'resource',
            title: 'Billing Channel',
            formControl: 'billingchannels',
            search: new BillingChannelConfig(true),
            required: false,
            multiple: true,
            check: 'equal',
            noCols: true,
          },
        ],
      },
    ],
  };
  resource = Notificationmanagementsdvp4m;
  resourceName = 'Notification';
  resourceKey = 'notificationmanagementsdvp4m';
  includes = [
    'firm.headquarterscountry',
    'countries',
    'billingchannels',
    'eventplatforms',
    'orderchannels',
    'offers.product',
    'offers.product.service',
    'offers.country',
    'products.service',
    'products.country',
    'services.ownerfirm.headquarterscountry',
    'handlerfirms.headquarterscountry',
    'ownerfirm.headquarterscountry',
  ];
  filters = [
    new NumberFilter('id', 'ID'),
    new StringFilter('notification_url', 'Notificaition URL'),
    new StringFilter('firm.name', 'Firm Name', true),
    new NumberFilter('firm.id', 'Firm ID'),
  ];
  search = {};
  constructor(private atom: AtomService) {}
}

// @Relationship() firm?: Firm;
// @Relationship() countries?: Country[];
// @Relationship() billingchannels?: BillingChannel[];
// @Relationship() iplookups?: Platform[];
// @Relationship() orderchannels?: OrderChannel[];
// @Relationship() eventplatforms?: Platform[];
// @Relationship() offers?: Offer[];
// @Relationship() products?: Product[];
// @Relationship() services?: Service[];
// @Relationship() handlerfirms?: Firm[];
// @Relationship() ownerfirm?: Firm;
